import React, { useImperativeHandle, useRef } from "react";
//#region types
import { TransactionListExtended } from "models/propTypes/accountingPropTypes";
import { WintSuperkollAPIModelAccounting, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { AxiosResponse } from "axios";
import { AccountingFormTypes } from "models/formTypes";
//#endregion
//#region components
import { TableAsync, Tags, useFilterContext, useSearchContext } from "components/common";
import VoucherInfo from "./voucherInfo";
import TransactionCorrespondent from "./correspondent";
import { useNavigate, useLocation } from "react-router-dom";
import { useTransactionService } from "serviceHooks";
//#endregion
import { UrlHelper, ServiceHelper } from "utils/helpers";
import { TableHandles } from "models/propTypes/simpleTableProps";

export interface TransactionsListHandle {
  exportAllItems: () => Promise<TransactionListExtended[]>;
}

const TransactionsList: React.ForwardRefRenderFunction<TransactionsListHandle> = (props, ref) => {
  const transactionService = useTransactionService();
  const location = useLocation();
  const navigate = useNavigate();
  const { filter } = useFilterContext<AccountingFormTypes.CompanyBooksReportFilter>();
  const searchContext = useSearchContext();
  const tableAsyncRef = useRef<TableHandles>(null);

  const filterFunc = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      transactionService.get(baseFilter, filter, searchContext?.searchText) as Promise<
        AxiosResponse<WintUtilsWebApi.ListResponse<TransactionListExtended>>
      >,
    [filter, searchContext?.searchText]
  );

  useImperativeHandle(ref, () => ({
    exportAllItems: async () => {
      const getApiFilter = tableAsyncRef.current?.getApiFilter;
      const numPerPage = 100;
      const getPage = (pageNumber: number) => {
        const apiFilter = getApiFilter?.(pageNumber, numPerPage) ?? {
          page: pageNumber,
          numPerPage: numPerPage,
          orderByProperty: "bookingDate",
          orderByDescending: true,
          thenByDescending: false,
          isAutoPaginating: false,
          includeSummary: false,
        };

        return transactionService.get(apiFilter, filter, searchContext?.searchText) as Promise<
          AxiosResponse<WintUtilsWebApi.ListResponse<TransactionListExtended>>
        >;
      };

      return ServiceHelper.getAllPages<TransactionListExtended>(getPage);
    },
  }));

  const onRowClick = (row: TransactionListExtended) => {
    if (row.voucherReferenceType === undefined || (!row.voucherReferenceId && !row.voucherId)) {
      return;
    }

    switch (row.voucherReferenceType!) {
      case WintSuperkollAPIModelAccounting.VoucherType.Invoice:
        navigate(UrlHelper.formatPath(`/Invoice/${row.voucherReferenceId}`), {
          state: { background: location, preserveSearch: true },
        });
        break;
      case WintSuperkollAPIModelAccounting.VoucherType.IncomingInvoice:
        navigate(UrlHelper.formatPath(`/IncomingInvoice/${row.voucherReferenceId}`), {
          state: { background: location, preserveSearch: true },
        });
        break;
      case WintSuperkollAPIModelAccounting.VoucherType.Receipt:
        navigate(UrlHelper.formatPath(`/Receipt/${row.voucherReferenceId}`), {
          state: { background: location, preserveSearch: true },
        });
        break;
      case WintSuperkollAPIModelAccounting.VoucherType.Unknown:
      case WintSuperkollAPIModelAccounting.VoucherType.GenericVoucher:
        navigate(UrlHelper.formatPath(`/Voucher/${row.voucherId}`), {
          state: { background: location, preserveSearch: true },
        });
        break;
      default:
        break;
    }
  };

  return (
    <TableAsync<TransactionListExtended>
      tableRef={tableAsyncRef}
      properties={{
        voucherNumber: {
          description: TranslationKeys.Common.Voucher,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) => (
            <VoucherInfo
              voucherNumber={value.voucherNumber}
              series={value.voucherSeriesShortName}
              seriesName={value.voucherSeriesName}
            />
          ),
          noWrap: true,
        },
        bookingDate: {
          description: TranslationKeys.Common.Date,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) => new Date(value.bookingDate),
          noWrap: true,
        },
        correspondentName: {
          description: TranslationKeys.Accounting.RecipientOrSender,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) => (
            <TransactionCorrespondent
              id={value.correspondentId}
              name={value.correspondentName}
              type={value.voucherReferenceType}
            />
          ),
          maxWidth: 150,
          noWrap: true,
          disableSort: true,
        },
        voucherText: {
          description: TranslationKeys.Common.TransactionText,
          noWrap: true,
          maxWidth: 150,
        },
        accountNumber: {
          description: TranslationKeys.Common.PostedAs,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) =>
            `${value.accountNumber} ${value.accountName ?? ""}`,
          noWrap: true,
          maxWidth: 150,
        },
        dimensions: {
          description: TranslationKeys.Common.Dimensions,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) => (
            <Tags tags={value.dimensions?.map((d) => d.name ?? "") ?? []} maxDisplayCount={1} />
          ),
          maxWidth: 120,
          noWrap: true,
          disableSort: true,
        },
        debit: {
          description: TranslationKeys.Common.Debit,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) =>
            value.amount >= 0 ? new Amount(CurrencyEnums.CurrencyCode.SEK, Math.abs(value.amount)) : undefined,
          alignment: "right",
          disableSort: true,
          classification: "amount",
        },
        credit: {
          description: TranslationKeys.Common.Credit,
          formatter: (value: WintSuperkollAPIModelAccounting.Transaction) =>
            value.amount < 0 ? new Amount(CurrencyEnums.CurrencyCode.SEK, Math.abs(value.amount)) : undefined,
          alignment: "right",
          disableSort: true,
          classification: "amount",
        },
      }}
      filter={filterFunc}
      orderByProperty="bookingDate"
      orderByDescending
      pagination="advanced"
      maxRowCount={10}
      rowClickHandler={onRowClick}
    />
  );
};

export default React.forwardRef(TransactionsList);
